'use client';

// Payout Eligibility · "Exemplo · funded account" (Figma 12071:59993 desktop
// 792×516, 12471:10142 mobile 366×764). Three criteria, all met, then the
// "All criteria met" banner. Every number is an illustrative example.
import type { ReactNode } from 'react';
import { CheckmarkIcon } from '../Icons';
import { ExampleLabel, FigureFrame } from '../primitives';

const CRITERIA = [
  {
    key: 'consistency',
    slug: 'consistency-rule',
    title: 'Consistency Rule',
    body: 'Your best day stays within the consistency limit.',
    value: 'Best day 22%',
    fill: 22 / 25,
    note: 'limit 25% · example',
  },
  {
    key: 'days',
    slug: 'minimum-trading-days',
    title: 'Minimum trading days',
    body: 'You traded on enough active days.',
    value: '10 / 10 days',
    fill: 1,
    note: 'minimum 10 · example',
  },
  {
    key: 'scalping',
    slug: 'scalping-rule',
    title: 'Scalping Rule',
    body: 'At least 50% of trades and profits held longer than 15 seconds.',
    value: '60% · 79%',
    fill: 1,
    note: 'both above 50% · example',
  },
] as const;

const TEXT = 'mb-0 text-[2rem] leading-[3rem]';

function Ok() {
  return (
    <div className="flex h-[3.5rem] w-[3.5rem] shrink-0 items-center justify-center rounded-[0.5rem] bg-[#228e71] text-white">
      <CheckmarkIcon className="h-[2rem] w-[2rem]" />
    </div>
  );
}

function Measure({ value, fill, note }: { value: string; fill: number; note: ReactNode }) {
  return (
    <div className="flex w-full flex-col gap-[1rem] lg:w-[27.5rem] lg:shrink-0">
      <div className="flex w-full items-start justify-between whitespace-nowrap text-[2rem] font-medium leading-[3rem]">
        <p className="mb-0 text-black">{value}</p>
        <p className="mb-0 text-[#228e71]">met</p>
      </div>
      <div className="h-[0.5rem] w-full overflow-hidden rounded-[0.25rem] bg-black/[0.08]">
        <div className="h-[0.5rem] rounded-[0.25rem] bg-[#228e71]" style={{ width: `${fill * 100}%` }} />
      </div>
      <p className={`${TEXT} whitespace-nowrap font-normal text-black/40`}>{note}</p>
    </div>
  );
}

export default function PayoutEligibility() {
  return (
    <FigureFrame data-ui="figure.payoutEligibility" padding="p-[2rem] lg:p-[3rem]" className="gap-[2.5rem] font-geist">
      <ExampleLabel>EXAMPLE · FUNDED ACCOUNT</ExampleLabel>

      {CRITERIA.map((c) => (
        <div
          key={c.key}
          data-ui={`figure.payoutEligibility.${c.key}`}
          className="flex w-full items-start gap-[2rem] rounded-[0.25rem] border border-black/10 p-[2rem] lg:items-center"
        >
          <Ok />
          <div className="flex min-w-0 flex-1 flex-col gap-[2rem] lg:flex-row lg:items-center">
            <div className="flex min-w-0 flex-col gap-[0.25rem] lg:flex-1">
              <a
                href={`?rule=${c.slug}`}
                className={`${TEXT} w-fit whitespace-nowrap font-medium text-[#228e71] underline [text-underline-position:from-font]`}
              >
                {c.title}
              </a>
              <p className={`${TEXT} font-normal text-black/64`}>{c.body}</p>
            </div>
            <Measure value={c.value} fill={c.fill} note={c.note} />
          </div>
        </div>
      ))}

      <div
        data-ui="figure.payoutEligibility.banner"
        className="flex w-full items-center gap-[2rem] rounded-[0.25rem] bg-[#228e71] px-[2rem] py-[1.75rem] text-white lg:justify-center lg:gap-[1rem]"
      >
        <CheckmarkIcon className="h-[2rem] w-[2rem] shrink-0" />
        <p className={`${TEXT} min-w-0 flex-1 font-medium lg:flex-none lg:whitespace-nowrap`}>
          All criteria met — you can request your payout
        </p>
      </div>
    </FigureFrame>
  );
}
